import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChartConfiguration, ChartData, ChartEvent } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

// Material Imports
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Inject } from '@angular/core';

import { Conversation } from '../../../services/conversation';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-engagement-frequency',
  standalone: true,
  imports: [
    CommonModule, FormsModule, BaseChartDirective, MatFormFieldModule,
    MatInputModule, MatDatepickerModule, MatButtonModule, 
    MatTableModule, MatCardModule, MatDialogModule, MatIcon
  ],
  templateUrl: './engagement-frequency.component.html',
  styleUrls: ['./engagement-frequency.component.scss']
})
export class EngagementFrequencyComponent implements OnInit {

  @Input() globalStartDate!: Date;
@Input() globalEndDate!: Date;

  bucketData: any[] = [];
  allRawRows: any[] = []; // Stores the full list for drill-down and CSV
 startDate = new Date();
  endDate = new Date();

  // Chart Config
  doughnutChartData: ChartData<'doughnut'> = { labels: [], datasets: [] };
  doughnutChartOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: { enabled: true }
    }
  };

  constructor(private conversationService: Conversation, private dialog: MatDialog) {
    this.startDate.setDate(this.endDate.getDate() - 30);
  }

  ngOnInit(): void {
    this.fetchEngagement();
  }

  ngOnChanges(changes: SimpleChanges) {
  if (changes['globalStartDate'] || changes['globalEndDate']) {
    this.startDate = this.globalStartDate;
    this.endDate = this.globalEndDate;
    this.fetchEngagement(); // Your existing fetch/load function
  }
}

  fetchEngagement(): void {
    const start = this.startDate.toISOString().split('T')[0];
    const end = this.endDate.toISOString().split('T')[0];

    this.conversationService.getEngagementFrequency(start, end).subscribe(res => {
      this.bucketData = res.buckets ?? [];
      this.allRawRows = res.data ?? [];

      this.doughnutChartData = {
        labels: this.bucketData.map(b => `${b.frequency_range} interactions`),
        datasets: [{
          data: this.bucketData.map(b => b.customer_count),
          backgroundColor: ['#6366f1', '#10b981', '#f59e0b', '#ef4444']
        }]
      };
    });
  }

  // Called when a chart segment is clicked
  onChartClick(event: { event?: ChartEvent; active?: any[] }): void {
    if (event.active && event.active.length > 0) {
      const index = event.active[0].index;
      const range = this.bucketData[index].frequency_range;
      this.openDrillDownModal(range);
    }
  }

  openDrillDownModal(range: string): void {
    // Filter raw data based on bucket logic
    const filtered = this.allRawRows.filter(row => {
      const c = row.count;
      if (range === '1') return c === 1;
      if (range === '2-5') return c >= 2 && c <= 5;
      if (range === '6-10') return c >= 6 && c <= 10;
      if (range === '10+') return c > 10;
      return false;
    });

    this.dialog.open(EngagementDetailDialog, {
      width: '600px',
      data: { range, list: filtered }
    });
  }

  downloadCSV(): void {
    const headers = ['Conversation ID', 'Interaction Count'];
    const csvContent = [
      headers.join(','),
      ...this.allRawRows.map(row => `${row.conversation_id},${row.count}`)
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `engagement_report.csv`;
    a.click();
  }
}

/** 2. Drill-down Modal Component **/
@Component({
  standalone: true,
  imports: [CommonModule, MatTableModule, MatButtonModule, MatDialogModule],
  template: `
    <h2 mat-dialog-title>Conversations with {{data.range}} Interactions</h2>
    <mat-dialog-content>
      <table mat-table [dataSource]="data.list" class="mat-elevation-z2 drill-down-table" style="width: 100%">
        <ng-container matColumnDef="id">
          <th mat-header-cell *matHeaderCellDef> Conversation ID </th>
          <td mat-cell *matCellDef="let row"> {{row.conversation_id}} </td>
        </ng-container>
        <ng-container matColumnDef="count">
          <th mat-header-cell *matHeaderCellDef> Total Interactions </th>
          <td mat-cell *matCellDef="let row"> {{row.count}} </td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="['id', 'count']"></tr>
        <tr mat-row *matRowDef="let row; columns: ['id', 'count'];"></tr>
      </table>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Close</button>
    </mat-dialog-actions>
  `,
  styles: [`
    mat-dialog-content { margin-bottom: 12px; }

    .drill-down-table {
      th.mat-mdc-header-cell {
        background: var(--brand-primary);
        color: #fff;
        font-weight: 600;
      }

      td.mat-mdc-cell {
        padding: 0.75rem 1rem;
        color: #334155;
      }

      tr.mat-mdc-row:hover {
        background: var(--brand-surface-alt);
      }
    }
  `]
})
export class EngagementDetailDialog {
  constructor(@Inject(MAT_DIALOG_DATA) public data: any) {}
}